import {
  CheckCircleIcon,
  PrinterIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { BookContext } from "../contexts/BookContext";
import { PrintOrder } from "../types/models/PrintOrder";

// Where a confirmed order is: confirmed, at the printer, then shipped once the
// printer gives a tracking link.
const STEPS = [
  { key: "confirmed", Icon: CheckCircleIcon },
  { key: "at_printer", Icon: PrinterIcon },
  { key: "shipped", Icon: TruckIcon },
] as const;

const currentStep = (order: PrintOrder) => {
  if (order.printer_tracking_url) return 2;
  if (order.sent_to_printer_at) return 1;
  return 0;
};

export const PrintTrackingCard: FC = () => {
  const { t, i18n } = useTranslation();
  const { print_orders } = useContext(BookContext);
  const orders = (print_orders ?? [])
    .filter((order) => order.confirmed_at)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

  if (orders.length === 0) return null;

  return (
    <div className="flex flex-col w-full p-4 sm:p-6 bg-white rounded-lg shadow-md">
      <p className="text-slate-700 text-xl sm:text-2xl font-bold">
        {t("print_tracking.title")}
      </p>
      <div className="mt-4 space-y-4">
        {orders.map((order) => {
          const step = currentStep(order);
          return (
            <div
              key={order.id}
              className="rounded-md bg-delicate-10 p-3 sm:p-4"
            >
              <p className="text-sm text-slate-600">
                {t("print_tracking.order_of", {
                  date: new Date(order.confirmed_at).toLocaleDateString(
                    i18n.language,
                  ),
                  count: order.quantity,
                })}
              </p>
              <ol className="mt-3 flex flex-col sm:flex-row gap-2 sm:gap-6">
                {STEPS.map(({ key, Icon }, index) => (
                  <li
                    key={key}
                    className={`flex items-center gap-2 text-sm sm:text-base ${
                      index <= step
                        ? "text-delicate-800 font-medium"
                        : "text-slate-400"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {t(`print_tracking.steps.${key}`)}
                  </li>
                ))}
              </ol>
              {order.printer_tracking_url && (
                <a
                  href={order.printer_tracking_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block mt-3 text-sm text-rust-50 underline"
                >
                  {order.printer_carrier
                    ? t("print_tracking.track_with", {
                        carrier: order.printer_carrier,
                      })
                    : t("print_tracking.track")}
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
